<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    fixed
    width="460"
    class="cart-drawer"
    @input="$emit('input', $event)"
  >
    <div class="cart-header">
      <button class="cart-header__back" type="button" @click="close">
        <v-icon size="18">mdi-arrow-left</v-icon>
        <span>Back</span>
      </button>
      <div class="cart-header__title">
        <p>Your selection</p>
        <h2>Shopping bag <span>({{ itemCount }})</span></h2>
      </div>
      <button class="cart-header__close" type="button" aria-label="Close bag" @click="close">
        <v-icon size="20">mdi-close</v-icon>
      </button>
    </div>

    <div v-if="!items.length" class="empty-cart">
      <div class="empty-cart__icon"><v-icon size="42">mdi-shopping-outline</v-icon></div>
      <h3>Your bag is empty</h3>
      <p>Discover a scent that feels like you.</p>
      <button type="button" @click="continueShopping">Explore fragrances</button>
    </div>

    <template v-else>
      <div class="cart-items">
        <article v-for="item in items" :key="item.cartKey" class="cart-item">
          <div class="cart-item__visual">
            <PerfumeBottle :name="item.name" :show-label="false" :scale="0.42" />
          </div>
          <div class="cart-item__info">
            <div class="cart-item__title">
              <div>
                <h3>{{ item.name }}</h3>
                <p>{{ item.variantLabel }}</p>
              </div>
              <button type="button" aria-label="Remove item" @click="$emit('remove', item.cartKey)">×</button>
            </div>
            <div class="cart-item__bottom">
              <div class="quantity">
                <button type="button" @click="$emit('quantity', { key: item.cartKey, change: -1 })">−</button>
                <span>{{ item.quantity }}</span>
                <button type="button" @click="$emit('quantity', { key: item.cartKey, change: 1 })">+</button>
              </div>
              <strong>{{ formatPrice(item.price * item.quantity) }}</strong>
            </div>
          </div>
        </article>
      </div>

      <div class="checkout">
        <div class="total">
          <span>Subtotal</span>
          <strong>{{ formatPrice(total) }}</strong>
        </div>
        <p class="checkout__hint">Pickup and delivery are currently available in Panjim.</p>

        <div class="segmented">
          <button
            type="button"
            :class="{ active: checkout.deliveryOption === 'pickup' }"
            @click="update('deliveryOption', 'pickup')"
          >
            Store pickup
          </button>
          <button
            type="button"
            :class="{ active: checkout.deliveryOption === 'delivery' }"
            @click="update('deliveryOption', 'delivery')"
          >
            Delivery
          </button>
        </div>

        <a v-if="checkout.deliveryOption === 'pickup'" :href="pickupLocation" class="pickup-link" target="_blank">
          <v-icon size="17">mdi-map-marker-outline</v-icon> View pickup location
        </a>
        <label v-else>
          Delivery location
          <input
            :value="checkout.mapsLink"
            type="url"
            placeholder="Paste your Google Maps link"
            @input="update('mapsLink', $event.target.value)"
          >
        </label>

        <label>
          Preferred date & time
          <input
            :value="checkout.deliveryDateTime"
            type="text"
            placeholder="e.g. Saturday after 4 PM"
            @input="update('deliveryDateTime', $event.target.value)"
          >
        </label>

        <label class="gift-toggle">
          <input
            :checked="checkout.isGift"
            type="checkbox"
            @change="update('isGift', $event.target.checked)"
          >
          <span>This order is a gift</span>
        </label>
        <label v-if="checkout.isGift">
          Gift note
          <textarea
            :value="checkout.giftMessage"
            rows="2"
            placeholder="Write a short note"
            @input="update('giftMessage', $event.target.value)"
          ></textarea>
        </label>

        <a :href="whatsappLink" target="_blank" rel="noopener" class="whatsapp-button">
          <v-icon color="white" size="21">mdi-whatsapp</v-icon>
          Order on WhatsApp
        </a>
        <p class="checkout__secure">Review and send your pre-filled order in WhatsApp.</p>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import PerfumeBottle from './PerfumeBottle.vue'
import { formatPrice } from '../data/products'
import { PICKUP_LOCATION } from '../utils/order'

export default {
  name: 'CartDrawer',
  components: { PerfumeBottle },
  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, required: true },
    checkout: { type: Object, required: true },
    whatsappLink: { type: String, required: true }
  },
  computed: {
    itemCount() {
      return this.items.reduce((count, item) => count + item.quantity, 0)
    },
    total() {
      return this.items.reduce((sum, item) => sum + item.price * item.quantity, 0)
    },
    pickupLocation() {
      return PICKUP_LOCATION
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen) document.addEventListener('keydown', this.onKeydown)
      else document.removeEventListener('keydown', this.onKeydown)
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    formatPrice,
    close() {
      this.$emit('input', false)
    },
    onKeydown(event) {
      if (event.key === 'Escape') this.close()
    },
    update(key, value) {
      this.$emit('update-checkout', { key, value })
    },
    continueShopping() {
      this.close()
      this.$emit('shop')
    }
  }
}
</script>

<style scoped>
/* Sits above the fixed site header (z-index 20) and the toast (30).
   Vuetify derives the backdrop's z-index from this element. */
.cart-drawer { z-index: 50; font-family: Inter, sans-serif; }
.cart-drawer >>> .v-navigation-drawer__content { display: flex; flex-direction: column; }
.cart-header {
  position: sticky;
  z-index: 2;
  top: 0;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 15px 18px;
  border-bottom: 1px solid #eee9e2;
  background: #fff;
}
.cart-header__back {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 13px 8px 10px;
  border: 1px solid #e2dbd2;
  border-radius: 999px;
  color: #4b443c;
  background: #fff;
  font-size: 11px;
  font-weight: 700;
}
.cart-header__close {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border: 0;
  border-radius: 50%;
  color: #6f665d;
  background: #f4f0ea;
}
.cart-header p { margin: 0 0 2px; color: #a16a47; font-size: 9px; font-weight: 800; letter-spacing: 1.4px; text-transform: uppercase; }
.cart-header h2 { margin: 0; color: #26221d; font-family: Georgia, serif; font-size: 21px; font-weight: 400; line-height: 1.1; }
.cart-header h2 span { color: #958a7e; font-family: Inter, sans-serif; font-size: 11px; }
.cart-item__title button { border: 0; background: transparent; color: #6f665d; font-size: 21px; font-weight: 300; }
.empty-cart { flex: 1; display: flex; align-items: center; justify-content: center; flex-direction: column; padding: 40px; text-align: center; background: #faf8f4; }
.empty-cart__icon { width: 92px; height: 92px; display: grid; place-items: center; border-radius: 50%; background: #eee8df; }
.empty-cart h3 { margin: 24px 0 6px; font-family: Georgia, serif; font-size: 27px; font-weight: 400; }
.empty-cart p { color: #81766a; font-size: 13px; }
.empty-cart > button { margin-top: 20px; padding: 13px 18px; border: 0; border-radius: 8px; color: #fff; background: #28241f; font-weight: 700; }
.cart-items { padding: 10px 28px; }
.cart-item { display: grid; grid-template-columns: 75px 1fr; gap: 15px; padding: 19px 0; border-bottom: 1px solid #eee9e2; }
.cart-item__visual { height: 96px; display: grid; place-items: center; background: linear-gradient(180deg, #e6dbcd, #d5c6b3); border-radius: 8px; }
.cart-item__info { display: flex; flex-direction: column; justify-content: space-between; }
.cart-item__title, .cart-item__bottom { display: flex; justify-content: space-between; gap: 12px; }
.cart-item h3 { margin: 1px 0 2px; color: #2a2520; font-family: Georgia, serif; font-size: 17px; font-weight: 400; }
.cart-item p { margin: 0; color: #8c8175; font-size: 10px; }
.cart-item__title button { font-size: 21px; }
.cart-item__bottom { align-items: center; }
.quantity { display: flex; align-items: center; border: 1px solid #ded7ce; border-radius: 6px; overflow: hidden; }
.quantity button, .quantity span { width: 29px; height: 27px; display: grid; place-items: center; border: 0; background: #fff; color: #655d54; font-size: 12px; }
.quantity span { width: 25px; }
.cart-item__bottom strong { font-size: 13px; }
.checkout { padding: 27px 30px 35px; background: #f7f4ef; }
.total { display: flex; justify-content: space-between; color: #28231e; font-family: Georgia, serif; font-size: 22px; }
.checkout__hint, .checkout__secure { color: #8a8075; font-size: 10px; line-height: 1.5; }
.segmented { display: grid; grid-template-columns: 1fr 1fr; gap: 5px; margin: 20px 0 15px; padding: 4px; border-radius: 9px; background: #e9e3da; }
.segmented button { padding: 10px 4px; border: 0; border-radius: 6px; color: #746a5f; background: transparent; font-size: 11px; font-weight: 700; }
.segmented button.active { color: #2d2822; background: #fff; box-shadow: 0 2px 8px rgba(40,32,24,.08); }
.checkout label { display: block; margin-top: 14px; color: #71675d; font-size: 10px; font-weight: 700; letter-spacing: .4px; }
.checkout input[type="text"], .checkout input[type="url"], .checkout textarea { width: 100%; margin-top: 6px; padding: 11px 12px; border: 1px solid #ddd5cb; border-radius: 7px; outline: 0; background: #fff; color: #2e2923; font: 12px Inter, sans-serif; resize: vertical; }
.pickup-link { display: flex; align-items: center; gap: 5px; color: #805a40; font-size: 11px; text-decoration: none; }
.gift-toggle { display: flex !important; align-items: center; gap: 8px; }
.gift-toggle input { accent-color: #aa6e48; }
.whatsapp-button { display: flex; align-items: center; justify-content: center; gap: 9px; margin-top: 23px; padding: 14px; border-radius: 8px; color: #fff; background: #247b51; font-size: 12px; font-weight: 800; text-decoration: none; }
.checkout__secure { margin: 9px 0 0; text-align: center; }
@media (max-width: 500px) {
  .cart-header, .checkout { padding-left: 20px; padding-right: 20px; }
  .cart-items { padding-left: 20px; padding-right: 20px; }
}
</style>
